<template>
  <div class="brand-tab">
    <div class="brand-container">
      <!-- Header -->
      <div class="brand-header">
          <div class="brand-title-section">
          <h1 class="brand-name">{{ brandData?.name || 'Brand' }}</h1>
          <p class="brand-subtitle">Brand Guidelines & Assets</p>
        </div>
        <div class="brand-actions">
          <template v-if="!editMode">
            <n-button type="primary" size="small" @click="enterEditMode">
              <template #icon>
                <n-icon>
                  <svg viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                  </svg>
                </n-icon>
              </template>
              Edit Brand
            </n-button>
            <n-button size="small" @click="refreshBrands">
              <template #icon>
                <n-icon>
                  <svg viewBox="0 0 24 24">
                    <path fill="currentColor" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
                  </svg>
                </n-icon>
              </template>
              Refresh
            </n-button>
          </template>
          <template v-else>
            <n-button type="primary" size="small" @click="saveBrand" :loading="saving">
              Save
            </n-button>
            <n-button size="small" @click="cancelEdit">
              Cancel
            </n-button>
          </template>
        </div>
      </div>

      <!-- Basic Information Section -->
      <n-card class="brand-section" title="Basic Information" style="margin-bottom: 24px">
        <n-spin :show="loading">
          <template v-if="editMode">
            <!-- Edit Mode -->
            <n-form :model="editForm" label-placement="left" label-width="140">
              <n-grid :cols="2" :x-gap="16">
                <n-gi>
                  <n-form-item label="Name">
                    <n-input v-model:value="editForm.name" placeholder="Brand name" />
                  </n-form-item>
                </n-gi>
                <n-gi>
                  <n-form-item label="Code">
                    <n-input v-model:value="editForm.code" placeholder="Brand code" />
                  </n-form-item>
                </n-gi>
                <n-gi :span="2">
                  <n-form-item label="Description">
                    <n-input v-model:value="editForm.description" type="textarea" placeholder="Brand description" :rows="2" />
                  </n-form-item>
                </n-gi>
              </n-grid>

              <n-divider title-placement="left" style="margin: 24px 0 16px 0">Contact Information</n-divider>
              <n-grid :cols="2" :x-gap="16">
                <n-gi>
                  <n-form-item label="Contact Name">
                    <n-input v-model:value="editForm.contactName" placeholder="Contact name" />
                  </n-form-item>
                </n-gi>
                <n-gi>
                  <n-form-item label="Contact Email">
                    <n-input v-model:value="editForm.contactEmail" placeholder="contact@example.com" />
                  </n-form-item>
                </n-gi>
                <n-gi>
                  <n-form-item label="Phone">
                    <n-input v-model:value="editForm.phone" placeholder="Phone number" />
                  </n-form-item>
                </n-gi>
                <n-gi>
                  <n-form-item label="Website">
                    <n-input v-model:value="editForm.website" placeholder="https://example.com" />
                  </n-form-item>
                </n-gi>
                <n-gi :span="2">
                  <n-form-item label="Address">
                    <n-input v-model:value="editForm.address" placeholder="Address" />
                  </n-form-item>
                </n-gi>
              </n-grid>

              <n-divider title-placement="left" style="margin: 24px 0 16px 0">Social Media</n-divider>
              <n-grid :cols="2" :x-gap="16">
                <n-gi>
                  <n-form-item label="Instagram Username">
                    <n-input v-model:value="editForm.igHandle" placeholder="@username or username" />
                  </n-form-item>
                </n-gi>
                <n-gi>
                  <n-form-item label="Facebook Username">
                    <n-input v-model:value="editForm.fbHandle" placeholder="username" />
                  </n-form-item>
                </n-gi>
              </n-grid>

              <n-divider title-placement="left" style="margin: 24px 0 16px 0">Marketing</n-divider>
              <n-form-item label="USP">
                <n-input v-model:value="editForm.usp" type="textarea" placeholder="Unique selling proposition" :rows="2" />
              </n-form-item>
              <n-form-item label="Notes">
                <n-input v-model:value="editForm.notes" type="textarea" placeholder="Additional notes" :rows="3" />
              </n-form-item>
            </n-form>
          </template>
          <template v-else>
            <!-- View Mode -->
            <div class="basic-info-view-single-line">
              <span v-if="brandData?.name" class="info-item-inline">
                <span class="info-label-inline">Name:</span>
                <span class="info-value-inline">{{ brandData.name }}</span>
              </span>
              <span v-if="brandData?.code" class="info-item-inline">
                <span class="info-label-inline">Code:</span>
                <span class="info-value-inline">{{ brandData.code }}</span>
              </span>
              <span v-if="brandData?.website && brandData.website.trim()" class="info-item-inline">
                <span class="info-label-inline">Web:</span>
                <a :href="brandData.website" target="_blank" rel="noopener noreferrer" class="info-link-inline">{{ brandData.website }}</a>
              </span>
              <span v-if="brandData?.fbHandle && brandData.fbHandle.trim()" class="info-item-inline">
                <span class="info-label-inline">FB:</span>
                <a 
                  :href="`https://facebook.com/${brandData.fbHandle}`" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="info-link-inline"
                >
                  {{ brandData.fbHandle }}
                </a>
              </span>
              <span v-if="brandData?.igHandle && brandData.igHandle.trim()" class="info-item-inline">
                <span class="info-label-inline">IG:</span>
                <a 
                  :href="`https://instagram.com/${brandData.igHandle.replace(/^@/, '')}`" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="info-link-inline"
                >
                  {{ brandData.igHandle.startsWith('@') ? brandData.igHandle : `@${brandData.igHandle}` }}
                </a>
              </span>
            </div>
          </template>
        </n-spin>
      </n-card>

      <!-- 3 Column Layout -->
      <div class="brand-grid">
        <!-- Column 1 -->
        <div class="brand-column">
          <!-- Logos Section -->
          <n-card class="brand-section" title="Logos">
            <template #header-extra>
              <n-button v-if="editMode" size="tiny" @click="addLogoUrl" type="primary" text>
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" /></svg></n-icon>
                </template>
                Add Logo
              </n-button>
            </template>
            <n-spin :show="loading">
              <div v-if="editMode && editFormLogoUrls.length === 0 && logoUrls.length === 0" class="empty-state">
                <p>No logos defined. Click "Add Logo" to add one.</p>
              </div>
              <template v-else>
                <!-- Edit Mode -->
                <div v-if="editMode" class="edit-mode-content">
                  <div v-for="(url, index) in editFormLogoUrls" :key="index" class="logo-edit-item">
                    <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 8px">
                      <n-input v-model:value="editFormLogoUrls[index]" placeholder="Logo URL" style="flex: 1" />
                      <n-button @click="removeLogoUrl(index)" size="small" type="error">Remove</n-button>
                    </div>
                    <img v-if="url" :src="url" :alt="`Logo ${index + 1}`" class="logo-preview-small" />
                  </div>
                </div>
                <!-- View Mode -->
                <div v-else class="logo-urls-display">
                  <div v-for="(url, index) in logoUrls" :key="index" class="logo-url-display-item">
                    <img v-if="url" :src="url" :alt="`Logo ${index + 1}`" class="logo-display-image" />
                    <div v-else class="logo-placeholder-text">Invalid URL</div>
                  </div>
                </div>
              </template>
            </n-spin>
          </n-card>

          <!-- Colors Section -->
          <n-card class="brand-section" title="Colors">
            <template #header-extra>
              <n-button v-if="editMode" size="tiny" @click="addColorPalette" type="primary" text>
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" /></svg></n-icon>
                </template>
                Add Palette
              </n-button>
            </template>
            <n-spin :show="loading">
              <div v-if="editMode && editFormColors.length === 0 && colorPalettes.length === 0" class="empty-state">
                <p>No color palettes defined. Click "Add Palette" to add one.</p>
              </div>
              <template v-else>
                <!-- Edit Mode -->
                <div v-if="editMode" class="edit-mode-content">
                  <div 
                    v-for="(palette, index) in editFormColors" 
                    :key="index"
                    class="palette-edit-section"
                  >
                    <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 12px">
                      <n-input v-model:value="palette.name" placeholder="Palette name" style="flex: 1" />
                      <n-button @click="removeColorPalette(index)" size="small" type="error">Remove Palette</n-button>
                    </div>
                    <div class="color-edit-list">
                      <div v-for="(color, colorIndex) in palette.colors" :key="colorIndex" class="color-edit-row">
                        <n-color-picker v-model:value="color.hex" size="small" />
                        <n-input v-model:value="color.name" placeholder="Color name (optional)" style="flex: 1" />
                        <n-button @click="removeColor(index, colorIndex)" size="small">×</n-button>
                      </div>
                    </div>
                    <n-button @click="addColor(index)" size="small" style="margin-top: 8px">Add Color</n-button>
                  </div>
                </div>
                <!-- View Mode -->
                <div v-else>
                  <div 
                    v-for="(palette, index) in colorPalettes" 
                    :key="index"
                    class="color-section"
                  >
                    <h4 class="color-group-title">{{ palette.name }}</h4>
                    <div class="color-row">
                      <div 
                        v-for="(color, colorIndex) in palette.colors" 
                        :key="colorIndex"
                        class="color-item-horizontal"
                        @click="copyColor(color.hex)"
                        :title="color.hex + (color.name ? ` - ${color.name}` : '')"
                      >
                        <div class="color-swatch-small" :style="{ backgroundColor: color.hex }" />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </n-spin>
          </n-card>
        </div>

        <!-- Column 2 -->
        <div class="brand-column">
          <!-- Typography Section -->
          <n-card class="brand-section" title="Typography">
            <template #header-extra>
              <n-button v-if="editMode" size="tiny" @click="addTypeface" type="primary" text>
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" /></svg></n-icon>
                </template>
                Add Typeface
              </n-button>
            </template>
            <n-spin :show="loading">
              <div v-if="editMode && editFormTypefaces.length === 0 && typefaces.length === 0" class="empty-state">
                <p>No typefaces defined. Click "Add Typeface" to add one.</p>
              </div>
              <template v-else>
                <!-- Edit Mode -->
                <div v-if="editMode" class="edit-mode-content">
                  <div 
                    v-for="(typeface, index) in editFormTypefaces" 
                    :key="index"
                    class="typeface-edit-item"
                  >
                    <div style="display: flex; gap: 8px; margin-bottom: 12px">
                      <n-input v-model:value="typeface.typefaceName" placeholder="Typeface name" style="flex: 1" />
                      <n-input v-model:value="typeface.fontId" placeholder="Font ID" style="flex: 1" />
                      <n-input v-model:value="typeface.foundry" placeholder="Foundry" style="flex: 1" />
                      <n-button @click="removeTypeface(index)" size="small" type="error">Remove</n-button>
                    </div>
                  </div>
                </div>
                <!-- View Mode -->
                <div v-else>
                  <div 
                    v-for="(typeface, index) in typefaces" 
                    :key="index"
                    class="type-sample"
                  >
                    <h3 class="type-heading">{{ typeface.typefaceName }}</h3>
                    <p class="type-example" :style="{ fontFamily: typeface.fontId }">
                      The quick brown fox jumps over the lazy dog
                    </p>
                    <p class="type-meta">{{ typeface.foundry }} · {{ typeface.fontId }}</p>
                  </div>
                </div>
              </template>
            </n-spin>
          </n-card>

          <!-- Voice Section -->
          <n-card class="brand-section" title="Voice">
            <n-spin :show="loading">
              <template v-if="editMode">
                <div class="edit-mode-content">
                  <n-form-item label="Voice Tone">
                    <n-input
                      v-model:value="editForm.voiceTone"
                      type="textarea"
                      placeholder="Voice tone and guidelines description"
                      :rows="4"
                    />
                  </n-form-item>
                </div>
              </template>
              <template v-else>
                <div class="voice-section">
                  <div v-if="brandData?.voiceTone" class="voice-trait">
                    <p class="trait-description">{{ brandData.voiceTone }}</p>
                  </div>
                </div>
              </template>
            </n-spin>
          </n-card>
        </div>

        <!-- Column 3 -->
        <div class="brand-column">
          <!-- Copy Section -->
          <n-card class="brand-section" title="Copy">
            <template #header-extra>
              <n-button v-if="editMode" size="tiny" @click="addTagline" type="primary" text>
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" /></svg></n-icon>
                </template>
                Add Tagline
              </n-button>
            </template>
            <n-spin :show="loading">
              <template v-if="editMode">
                <div class="edit-mode-content">
                  <n-form-item label="Taglines">
                    <div v-for="(_, index) in editFormTaglines" :key="index" class="tagline-edit-item">
                      <div style="display: flex; gap: 8px; margin-bottom: 8px">
                        <n-input v-model:value="editFormTaglines[index]" placeholder="Tagline" style="flex: 1" />
                        <n-button @click="removeTagline(index)" size="small" type="error">×</n-button>
                      </div>
                    </div>
                  </n-form-item>
                  <n-form-item label="Elevator Pitch" style="margin-top: 16px">
                    <n-input
                      v-model:value="editForm.elevatorPitch"
                      type="textarea"
                      placeholder="Elevator pitch"
                      :rows="3"
                    />
                  </n-form-item>
                </div>
              </template>
              <template v-else>
                <div class="copy-section">
                  <div v-if="taglines.length > 0" class="copy-item">
                    <h4 class="copy-title">Taglines</h4>
                    <ul class="copy-list">
                      <li v-for="(tagline, index) in taglines" :key="index">{{ tagline }}</li>
                    </ul>
                  </div>
                  <div v-if="brandData?.elevatorPitch" class="copy-item">
                    <h4 class="copy-title">Elevator Pitch</h4>
                    <p class="copy-text">{{ brandData.elevatorPitch }}</p>
                  </div>
                </div>
              </template>
            </n-spin>
          </n-card>

          <!-- Team Section -->
          <n-card class="brand-section" title="Team">
            <template #header-extra>
              <n-button v-if="editMode" size="tiny" @click="addTeamMember" type="primary" text>
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" /></svg></n-icon>
                </template>
                Add Member
              </n-button>
            </template>
            <n-spin :show="loading">
              <div v-if="editMode && editFormTeamMembers.length === 0 && teamMembers.length === 0" class="empty-state">
                <p>No team members defined. Click "Add Member" to add one.</p>
              </div>
              <template v-else>
                <!-- Edit Mode -->
                <div v-if="editMode" class="edit-mode-content">
                  <div 
                    v-for="(member, index) in editFormTeamMembers" 
                    :key="index"
                    class="team-member-edit-item"
                  >
                    <div style="display: flex; gap: 8px; margin-bottom: 12px">
                      <n-input v-model:value="member.name" placeholder="Name" style="flex: 1" />
                      <n-input v-model:value="member.role" placeholder="Role" style="flex: 1" />
                      <n-input v-model:value="member.initials" placeholder="Initials" style="width: 100px" />
                      <n-button @click="removeTeamMember(index)" size="small" type="error">Remove</n-button>
                    </div>
                  </div>
                </div>
                <!-- View Mode -->
                <div v-else class="team-section">
                  <div 
                    v-for="(member, index) in teamMembers" 
                    :key="index"
                    class="team-member"
                  >
                    <div class="member-avatar">
                      <span>{{ member.initials }}</span>
                    </div>
                    <div class="member-info">
                      <p class="member-name">{{ member.name }}</p>
                      <p class="member-role">{{ member.role }}</p>
                    </div>
                  </div>
                </div>
              </template>
            </n-spin>
          </n-card>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useMessage, NCard, NButton, NIcon, NSpin, NForm, NFormItem, NInput, NColorPicker, NGrid, NGi } from 'naive-ui'
import { apiClient } from '@/services/api'

const message = useMessage()

// Brand ID - will be set from props or fetched
const currentBrandId = ref<string | null>(null)

// Brand data state
const brandData = ref<any>(null)
const loading = ref(false)
const editMode = ref(false)
const saving = ref(false)

// Parsed data from JSON fields
const colorPalettes = computed(() => {
  if (!brandData.value?.colors) return []
  try {
    const parsed = typeof brandData.value.colors === 'string' 
      ? JSON.parse(brandData.value.colors) 
      : brandData.value.colors
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

const typefaces = computed(() => {
  if (!brandData.value?.typefaces) return []
  try {
    const parsed = typeof brandData.value.typefaces === 'string'
      ? JSON.parse(brandData.value.typefaces)
      : brandData.value.typefaces
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

const taglines = computed(() => {
  if (!brandData.value?.taglines) return []
  try {
    const parsed = typeof brandData.value.taglines === 'string'
      ? JSON.parse(brandData.value.taglines)
      : brandData.value.taglines
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

const teamMembers = computed(() => {
  if (!brandData.value?.teamMembers) return []
  try {
    const parsed = typeof brandData.value.teamMembers === 'string'
      ? JSON.parse(brandData.value.teamMembers)
      : brandData.value.teamMembers
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

const logoUrls = computed(() => {
  if (!brandData.value?.logoUrls) return []
  try {
    const parsed = typeof brandData.value.logoUrls === 'string'
      ? JSON.parse(brandData.value.logoUrls)
      : brandData.value.logoUrls
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

// Edit form data
const editForm = ref({
  name: '',
  code: '',
  description: '',
  contactName: '',
  contactEmail: '',
  phone: '',
  address: '',
  website: '',
    igHandle: '',
    fbHandle: '',
    voiceTone: '',
    elevatorPitch: '',
  usp: '',
  notes: ''
})

const editFormColors = ref<Array<{ name: string; colors: Array<{ name?: string; hex: string }> }>>([])
const editFormLogoUrls = ref<string[]>([])
const editFormTypefaces = ref<Array<{ typefaceName: string; fontId: string; foundry: string }>>([])
const editFormTaglines = ref<string[]>([])
const editFormTeamMembers = ref<Array<{ name: string; role: string; initials: string }>>([])


// Load brand data
const loadBrand = async () => {
  if (!currentBrandId.value) {
    // Try to get first brand if none selected
    try {
      const response = await apiClient.getBrands({ page: 1, limit: 1 })
      if (response.brands && response.brands.length > 0) {
        currentBrandId.value = response.brands[0].id
        await loadBrandById(response.brands[0].id)
        return
      }
    } catch (error) {
      console.error('Failed to fetch brands:', error)
    }
    return
  }
  await loadBrandById(currentBrandId.value)
}

const loadBrandById = async (id: string) => {
  loading.value = true
  try {
    const response = await apiClient.getBrand(id)
    // Update brandData - this will trigger reactive updates in computed properties
    brandData.value = { ...response.brand }
    // Also update the edit form so it's in sync
    populateEditForm(response.brand)
  } catch (error: any) {
    message.error(error.response?.data?.message || 'Failed to load brand')
    console.error('Failed to load brand:', error)
  } finally {
    loading.value = false
  }
}

const populateEditForm = (brand: any) => {
  editForm.value = {
    name: brand.name || '',
    code: brand.code || '',
    description: brand.description || '',
    contactName: brand.contactName || '',
    contactEmail: brand.contactEmail || '',
    phone: brand.phone || '',
    address: brand.address || '',
    website: brand.website || '',
    igHandle: brand.igHandle || '',
    fbHandle: brand.fbHandle || '',
    voiceTone: brand.voiceTone || '',
    elevatorPitch: brand.elevatorPitch || '',
    usp: brand.usp || '',
    notes: brand.notes || ''
  }

  // Parse JSON fields
  try {
    editFormColors.value = brand.colors 
      ? (typeof brand.colors === 'string' ? JSON.parse(brand.colors) : brand.colors)
      : []
    if (!Array.isArray(editFormColors.value)) editFormColors.value = []
  } catch {
    editFormColors.value = []
  }

  try {
    editFormLogoUrls.value = brand.logoUrls
      ? (typeof brand.logoUrls === 'string' ? JSON.parse(brand.logoUrls) : brand.logoUrls)
      : []
    if (!Array.isArray(editFormLogoUrls.value)) editFormLogoUrls.value = []
  } catch {
    editFormLogoUrls.value = []
  }

  try {
    editFormTypefaces.value = brand.typefaces
      ? (typeof brand.typefaces === 'string' ? JSON.parse(brand.typefaces) : brand.typefaces)
      : []
    if (!Array.isArray(editFormTypefaces.value)) editFormTypefaces.value = []
  } catch {
    editFormTypefaces.value = []
  }

  try {
    editFormTaglines.value = brand.taglines
      ? (typeof brand.taglines === 'string' ? JSON.parse(brand.taglines) : brand.taglines)
      : []
    if (!Array.isArray(editFormTaglines.value)) editFormTaglines.value = []
  } catch {
    editFormTaglines.value = []
  }

  try {
    editFormTeamMembers.value = brand.teamMembers
      ? (typeof brand.teamMembers === 'string' ? JSON.parse(brand.teamMembers) : brand.teamMembers)
      : []
    if (!Array.isArray(editFormTeamMembers.value)) editFormTeamMembers.value = []
  } catch {
    editFormTeamMembers.value = []
  }
}

// Color palette management
const addColorPalette = () => {
  editFormColors.value.push({ name: '', colors: [{ hex: '#000000' }] })
}

const removeColorPalette = (index: number) => {
  editFormColors.value.splice(index, 1)
}

const addColor = (paletteIndex: number) => {
  editFormColors.value[paletteIndex].colors.push({ hex: '#000000' })
}

const removeColor = (paletteIndex: number, colorIndex: number) => {
  editFormColors.value[paletteIndex].colors.splice(colorIndex, 1)
}

// Logo URL management
const addLogoUrl = () => {
  editFormLogoUrls.value.push('')
}

const removeLogoUrl = (index: number) => {
  editFormLogoUrls.value.splice(index, 1)
}

// Typeface management
const addTypeface = () => {
  editFormTypefaces.value.push({ typefaceName: '', fontId: '', foundry: '' })
}

const removeTypeface = (index: number) => {
  editFormTypefaces.value.splice(index, 1)
}

// Tagline management
const addTagline = () => {
  editFormTaglines.value.push('')
}

const removeTagline = (index: number) => {
  editFormTaglines.value.splice(index, 1)
}

// Team member management
const addTeamMember = () => {
  editFormTeamMembers.value.push({ name: '', role: '', initials: '' })
}

const removeTeamMember = (index: number) => {
  editFormTeamMembers.value.splice(index, 1)
}

// Edit mode functions
const enterEditMode = () => {
  if (brandData.value) {
    populateEditForm(brandData.value)
  } else {
    // Reset form for new brand
    editForm.value = {
      name: '',
      code: '',
      description: '',
      contactName: '',
      contactEmail: '',
      phone: '',
      address: '',
      website: '',
    igHandle: '',
    fbHandle: '',
    voiceTone: '',
    elevatorPitch: '',
      usp: '',
      notes: ''
    }
    editFormColors.value = []
    editFormLogoUrls.value = []
    editFormTypefaces.value = []
    editFormTaglines.value = []
    editFormTeamMembers.value = []
  }
  editMode.value = true
}

const cancelEdit = () => {
  editMode.value = false
  // Reset form to original data
  if (brandData.value) {
    populateEditForm(brandData.value)
  }
}

// Save brand
const saveBrand = async () => {
  // Basic validation
  if (!editForm.value.name || !editForm.value.code) {
    message.error('Name and Code are required')
    return
  }

  saving.value = true
  try {
    const data: any = {
      ...editForm.value,
      // Always include array fields, even if empty (will be stored as "[]")
      colors: editFormColors.value,
      logoUrls: editFormLogoUrls.value,
      typefaces: editFormTypefaces.value,
      taglines: editFormTaglines.value,
      teamMembers: editFormTeamMembers.value
    }

    // Remove empty strings and convert to undefined for optional fields
    Object.keys(data).forEach(key => {
      if (data[key] === '') {
        data[key] = undefined
      }
    })

    let response
    if (brandData.value?.id) {
      // Update existing brand
      response = await apiClient.updateBrand(brandData.value.id, data)
      message.success('Brand updated successfully')
      // Reload from server to ensure we have the latest data
      await loadBrandById(brandData.value.id)
    } else {
      // Create new brand
      response = await apiClient.createBrand(data)
      currentBrandId.value = response.brand.id
      message.success('Brand created successfully')
      // Reload from server to ensure we have the latest data
      await loadBrandById(response.brand.id)
    }
    
    // Exit edit mode after successful save
    editMode.value = false
  } catch (error: any) {
    message.error(error.response?.data?.message || 'Failed to save brand')
    console.error('Failed to save brand:', error)
  } finally {
    saving.value = false
  }
}

const refreshBrands = async () => {
  await loadBrand()
  message.success('Brand refreshed')
}

const copyColor = async (hex: string) => {
  try {
    await navigator.clipboard.writeText(hex)
    message.success(`Copied ${hex} to clipboard`)
  } catch (error) {
    message.error('Failed to copy color')
  }
}

// Handle brand change event from Layout
const handleBrandProjectChanged = async (event: Event) => {
  const customEvent = event as CustomEvent<{ brandId?: string | null; projectId?: string | null }>
  const brandId = customEvent?.detail?.brandId || localStorage.getItem('selectedBrandId')
  
  if (brandId && brandId !== currentBrandId.value) {
    currentBrandId.value = brandId
    await loadBrandById(brandId)
  } else if (!brandId) {
    currentBrandId.value = null
    brandData.value = null
  }
}

// Load brand on mount
onMounted(() => {
  // Listen for brand changes from Layout
  window.addEventListener('brand-project-changed', handleBrandProjectChanged)
  
  // Try to get selected brand from localStorage first
  const savedBrandId = localStorage.getItem('selectedBrandId')
  if (savedBrandId) {
    currentBrandId.value = savedBrandId
    loadBrandById(savedBrandId)
  } else {
    // Fallback to loading first brand if none selected
    loadBrand()
  }
})

// Cleanup event listener
onUnmounted(() => {
  window.removeEventListener('brand-project-changed', handleBrandProjectChanged)
})
</script>

<style scoped>
.brand-tab {
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  background: var(--base-color);
}

.brand-container {
  max-width: 1400px;
  margin: 0 auto;
}

.brand-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding: 24px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.brand-title-section {
  flex: 1;
}

.brand-name {
  margin: 0 0 4px 0;
  font-size: 32px;
  font-weight: 700;
  color: var(--text-color);
}

.brand-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-color-2);
}

.brand-actions {
  display: flex;
  gap: 12px;
}

/* 3 Column Grid Layout */
.brand-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

@media (max-width: 1200px) {
  .brand-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .brand-grid {
    grid-template-columns: 1fr;
  }
}

.brand-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.brand-section {
  width: 100%;
}

/* Logos Section */
.logo-container {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.logo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  flex: 1;
  align-items: start;
}

.logo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  height: 100%;
}

.logo-preview {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  transition: all 0.2s ease;
}

/* Dark variant background */
.logo-preview.dark {
  background: #1a1a1a;
}

.logo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--card-color);
  border-radius: 4px;
  transition: all 0.2s ease;
}

.logo-placeholder.dark {
  background: #2a2a2a;
}

.logo-text {
  font-weight: 700;
  color: var(--text-color);
  letter-spacing: 1px;
  transition: all 0.2s ease;
}

/* Horizontal logo text */
.logo-text.horizontal {
  font-size: 14px;
}

.logo-text.horizontal.sm {
  font-size: 10px;
}

/* Square logo text */
.logo-text.square {
  font-size: 12px;
}

.logo-text.square.sm {
  font-size: 8px;
}

/* Vertical logo text - rotated */
.logo-text.vertical {
  font-size: 12px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.logo-text.vertical.sm {
  font-size: 8px;
}

.logo-placeholder.dark .logo-text {
  color: #ffffff;
}

.logo-label {
  margin: 0;
  font-size: 12px;
  color: var(--text-color-2);
  text-align: center;
  font-weight: 500;
}

/* Variant dots (pagination style) */
.logo-variants {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
}

.variant-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: none;
  background: var(--border-color);
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
}

.variant-dot:hover {
  background: var(--text-color-2);
  transform: scale(1.2);
}

.variant-dot.active {
  background: var(--primary-color);
  width: 8px;
  height: 8px;
}

/* Side variant buttons (icons) */
.logo-variants-side {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  padding: 8px;
}

.variant-button-icon {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--base-color);
  color: var(--text-color-2);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.variant-button-icon:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.05);
  transform: translateY(-2px);
}

.variant-button-icon.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
}

/* Colors Section */
.color-section {
  margin-bottom: 24px;
}

.color-section:last-child {
  margin-bottom: 0;
}

.color-group-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.color-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
}

.color-item-horizontal {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  border-radius: 2px;
}

.color-item-horizontal:hover {
  background: rgba(24, 160, 88, 0.05);
  transform: translateY(-2px);
}

.color-swatch-small {
  width: 20px;
  height: 20px;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.color-item-horizontal:hover .color-swatch-small {
  transform: scale(1.1);
}

.project-palette {
  margin-bottom: 20px;
}

.project-palette:last-child {
  margin-bottom: 0;
}

.palette-name {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-color);
}

/* Typography Section */
.typography-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.type-sample {
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
}

.type-sample:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.type-heading {
  margin: 0 0 12px 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-color-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-example {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: var(--text-color);
}

.type-example.heading {
  font-size: 32px;
  font-weight: 700;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.type-example.body {
  font-size: 16px;
  font-weight: 400;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.6;
}

.type-example.mono {
  font-size: 14px;
  font-family: 'JetBrains Mono', 'Monaco', 'Menlo', monospace;
}

.type-meta {
  margin: 0;
  font-size: 12px;
  color: var(--text-color-2);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Voice Section */
.voice-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.voice-trait {
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color);
}

.voice-trait:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.trait-title {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.trait-description {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-color-2);
}

.trait-list {
  margin: 8px 0 0 0;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.8;
  color: var(--text-color-2);
}

.trait-list li {
  margin: 4px 0;
}

/* Copy Section */
.copy-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.copy-item {
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color);
}

.copy-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.copy-title {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.copy-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-color-2);
  font-style: italic;
}

.copy-list {
  margin: 8px 0 0 0;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.8;
  color: var(--text-color-2);
}

.copy-list li {
  margin: 4px 0;
}

/* Team Section */
.team-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.team-member {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.team-member:hover {
  border-color: var(--primary-color);
  background: rgba(24, 160, 88, 0.05);
}

.member-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
}

.member-info {
  flex: 1;
}

.member-name {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
}

.member-role {
  margin: 0;
  font-size: 12px;
  color: var(--text-color-2);
}

/* Edit Form Styles */
.empty-state {
  padding: 24px;
  text-align: center;
  color: var(--text-color-2);
}

.palette-edit-item {
  margin-bottom: 16px;
  padding: 12px;
  background: var(--base-color);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.color-edit-item {
  margin-bottom: 8px;
}

.logo-url-item {
  margin-bottom: 8px;
}

.typeface-edit-item {
  margin-bottom: 8px;
}

.voice-guideline-item {
  margin-bottom: 8px;
}

.tagline-item {
  margin-bottom: 8px;
}

.team-member-edit-item {
  margin-bottom: 8px;
}

.logo-urls-display {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

.logo-url-display-item {
  aspect-ratio: 1;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--base-color);
}

.logo-display-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.logo-placeholder-text {
  color: var(--text-color-2);
  font-size: 12px;
}

.brand-section-edit {
  margin-bottom: 24px;
}

.edit-mode-content {
  padding: 8px 0;
}

.palette-edit-section {
  margin-bottom: 20px;
  padding: 12px;
  background: var(--base-color);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.color-edit-list {
  margin-top: 12px;
}

.color-edit-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.logo-edit-item {
  margin-bottom: 16px;
}

.logo-preview-small {
  max-width: 150px;
  max-height: 150px;
  margin-top: 8px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 4px;
}

.voice-guideline-edit-item {
  margin-bottom: 8px;
}

/* Basic Information View Mode - Single Line */
.basic-info-view-single-line {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  align-items: center;
  padding: 8px 0;
}

.info-item-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.info-label-inline {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-color-2);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.info-value-inline {
  font-size: 14px;
  color: var(--text-color);
}

.info-link-inline {
  font-size: 14px;
  color: var(--primary-color);
  text-decoration: none;
  word-break: break-all;
}

.info-link-inline:hover {
  text-decoration: underline;
}
</style>

